import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import backgroundImage from '../../images/background.png';
import slideImage2     from '../../images/second_slide_2.png';
import slideImage3     from '../../images/second_slide_3.png';
import slideImage4     from '../../images/second_slide_4.png';
import { getBanner }   from '../../../api/getBanner';
import { API_URL }     from '../../../config/connect';

const resolveBannerUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const base = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    return `${base}${url.startsWith('/') ? url : `/${url}`}`;
};

export default function Second() {
    const { t }    = useTranslation();
    const navigate = useNavigate();

    const [current,   setCurrent]   = useState(0);
    const [paused,    setPaused]    = useState(false);
    const [imgErr,    setImgErr]    = useState({});
    const [bannerUrl, setBannerUrl] = useState('');

    /* fetch dynamic banner */
    useEffect(() => {
        let cancelled = false;
        getBanner()
            .then(res => {
                const url =
                    res?.banner?.image_url || res?.banner?.background_image ||
                    res?.banner?.url       || res?.image_url ||
                    res?.background_image  || res?.banner_url || res?.url || '';
                if (!cancelled && url) setBannerUrl(resolveBannerUrl(url));
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const slides = [
        { id: 1, titleKey: 'secondSlide1Title', descKey: 'secondSlide1Description', img: bannerUrl || backgroundImage },
        { id: 2, titleKey: 'secondSlide2Title', descKey: 'secondSlide2Description', img: slideImage2 },
        { id: 3, titleKey: 'secondSlide3Title', descKey: 'secondSlide3Description', img: slideImage3 },
        { id: 4, titleKey: 'secondSlide4Title', descKey: 'secondSlide4Description', img: slideImage4 },
    ];

    const goTo = useCallback(
        (idx) => setCurrent((idx + slides.length) % slides.length),
        [slides.length]
    );

    /* auto-play */
    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => goTo(current + 1), 5000);
        return () => clearInterval(id);
    }, [paused, current, goTo]);

    return (
        /* ── wrapper: h-[260px] mobile → h-[420px] desktop ── */
        <div className="relative w-full h-[260px] md:h-[420px] overflow-hidden bg-black">

            {/* ── slides track ───────────────────────────────── */}
            <div
                className="flex h-full transition-transform duration-700 ease-in-out"
                style={{ transform: `translateX(-${current * 100}%)` }}
            >
                {slides.map((slide, i) => (
                    <div key={slide.id} className="relative w-full h-full shrink-0">

                        {/* background */}
                        <img
                            src={imgErr[slide.id] ? backgroundImage : (slide.img || backgroundImage)}
                            alt=""
                            loading={i === 0 ? 'eager' : 'lazy'}
                            onError={() => setImgErr(p => ({ ...p, [slide.id]: true }))}
                            className="absolute inset-0 w-full h-full object-cover"
                        />

                        {/* gradient overlay — dark at bottom */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

                        {/* text — pinned to bottom-left */}
                        <div className="absolute inset-0 flex items-end">
                            <div className="w-full max-w-[1400px] mx-auto px-5 md:px-[60px] lg:px-[120px] xl:px-[200px] pb-10 md:pb-16">
                                <div className="max-w-xl">
                                    <h2 className="text-white text-xl md:text-3xl font-bold leading-snug mb-1 md:mb-2">
                                        {t(slide.titleKey)}
                                    </h2>
                                    <p className="text-white/75 text-xs md:text-base leading-relaxed line-clamp-2 mb-3 md:mb-4">
                                        {t(slide.descKey)}
                                    </p>
                                    <button
                                        onClick={() => navigate('/notifications')}
                                        className="
                                            inline-flex items-center gap-1.5
                                            border border-white/50 hover:border-white
                                            text-white text-xs md:text-sm font-semibold
                                            px-4 py-2 rounded-full
                                            bg-white/10 hover:bg-white/20
                                            transition-all duration-150 cursor-pointer
                                        "
                                    >
                                        {t('readMore')}
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                            <path d="M9 5l7 7-7 7" stroke="white" strokeWidth="2.5"
                                                strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── slide counter — desktop only ──────────────── */}
            <div className="absolute top-5 right-5 z-20 hidden md:flex items-center gap-1">
                <span className="text-white text-lg font-bold tabular-nums">
                    {String(current + 1).padStart(2, '0')}
                </span>
                <span className="text-white/40 text-sm mx-1">/</span>
                <span className="text-white/50 text-sm tabular-nums">
                    {String(slides.length).padStart(2, '0')}
                </span>
            </div>

            {/* ── pause / play ───────────────────────────────── */}
            <button
                onClick={() => setPaused(p => !p)}
                aria-label={paused ? t('playSlideshow') : t('pauseSlideshow')}
                className="
                    absolute top-4 right-4 z-20 md:top-14 md:right-5
                    w-8 h-8 rounded-full
                    bg-black/30 hover:bg-black/50
                    border border-white/20 hover:border-white/40
                    flex items-center justify-center
                    transition-all duration-150 cursor-pointer
                "
            >
                {paused
                    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                    : <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" /></svg>
                }
            </button>

            {/* ── prev arrow ──────────────────────────────────── */}
            <button
                onClick={() => goTo(current - 1)}
                aria-label={t('prevSlide')}
                className="
                    absolute left-3 top-1/2 -translate-y-1/2 z-20
                    w-8 h-8 md:w-10 md:h-10 rounded-full
                    bg-black/30 hover:bg-black/50
                    border border-white/20 hover:border-white/50
                    flex items-center justify-center
                    transition-all duration-150 cursor-pointer
                "
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M15 19l-7-7 7-7" stroke="white" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {/* ── next arrow ──────────────────────────────────── */}
            <button
                onClick={() => goTo(current + 1)}
                aria-label={t('nextSlide')}
                className="
                    absolute right-3 top-1/2 -translate-y-1/2 z-20
                    w-8 h-8 md:w-10 md:h-10 rounded-full
                    bg-black/30 hover:bg-black/50
                    border border-white/20 hover:border-white/50
                    flex items-center justify-center
                    transition-all duration-150 cursor-pointer
                "
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 5l7 7-7 7" stroke="white" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {/* ── dot indicators ──────────────────────────────── */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
                {slides.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => goTo(i)}
                        aria-label={t('goToSlide', { index: i + 1 })}
                        className={[
                            'h-1.5 rounded-full transition-all duration-300 cursor-pointer border-none',
                            i === current ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70',
                        ].join(' ')}
                    />
                ))}
            </div>
        </div>
    );
}
